import axios from 'axios';
import React from 'react';
import { domToReact, Element, HTMLReactParserOptions } from 'html-react-parser';
import { documentUrl, fileUrl } from 'src/shared/services/utils/url';

// Make stored news HTML safe by removing embedded access tokens and
// normalizing document links to clean non-tokenized URLs.
export const normalizeDocumentLinksForRender = (content: string): string => {
  if (!content) {
    return content;
  }

  // Replace any tokenized document URLs in visible text or attributes with a clean URL.
  const tokenizedUrlPattern =
    /https?:\/\/[^"'<\s]+\/v1\/Document\/(\d+)\/?Download\?access_token=[^"'<\s)]+/gi;
  let normalizedContent = content.replace(tokenizedUrlPattern, (_match, id) => {
    return `${documentUrl(id)}/`;
  });

  // For anchor href pointing to /v1/Document/{id} (or /Download variants),
  // normalize to clean non-tokenized document URL.
  const hrefPattern =
    /href=(['"])(https?:\/\/[^'"]*\/v1\/Document\/(\d+)(?:\/Download(?:\?[^'"]*)?|\/?)[^'"]*)\1/gi;
  normalizedContent = normalizedContent.replace(
    hrefPattern,
    (_match, quote, _href, id) => `href=${quote}${documentUrl(id)}/${quote}`,
  );

  return normalizedContent;
};

export const extractDocumentIdFromUrl = (href?: string): string | undefined => {
  if (!href) {
    return undefined;
  }
  const match = href.match(/\/v1\/Document\/(\d+)(?:\/|$)/i);
  return match?.[1];
};

export const openDocumentLinkWithValidation = async (
  documentId: string,
  token: string | undefined,
  onError: () => void,
) => {
  if (!token) {
    onError();
    return;
  }

  try {
    const res = await axios.get(`${fileUrl(documentId, token)}&dl=1`, {
      responseType: 'blob',
    });
    const blobUrl = window.URL.createObjectURL(
      new Blob([res.data], {
        type: res.headers['content-type'],
      }),
    );
    const link = document.createElement('a');
    link.href = blobUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => {
      window.URL.revokeObjectURL(blobUrl);
    }, 60_000);
  } catch (error) {
    onError();
  }
};

type CreateNewsContentParserOptionsArgs = {
  token?: string;
  onUnavailable: () => void;
  onDownload?: (documentId: string) => void;
  stripImages?: boolean;
};

export const createNewsContentParserOptions = ({
  token,
  onUnavailable,
  onDownload,
  stripImages = false,
}: CreateNewsContentParserOptionsArgs): HTMLReactParserOptions => {
  const parserOptions: HTMLReactParserOptions = {
    replace: (domNode) => {
      if (
        stripImages &&
        domNode instanceof Element &&
        domNode.type === 'tag' &&
        domNode.name === 'img'
      ) {
        return React.createElement(React.Fragment, null);
      }

      if (
        !(domNode instanceof Element) ||
        domNode.type !== 'tag' ||
        domNode.name !== 'a'
      ) {
        return domNode;
      }

      const href = domNode.attribs?.href;
      const documentId = extractDocumentIdFromUrl(href);
      if (!documentId) {
        return domNode;
      }

      return React.createElement(
        'a',
        {
          href,
          target: '_blank',
          rel: 'noopener noreferrer',
          onClick: (event: React.MouseEvent<HTMLAnchorElement>) => {
            event.preventDefault();
            openDocumentLinkWithValidation(documentId, token, onUnavailable)
              .then(() => onDownload?.(documentId))
              .catch(() => undefined);
          },
        },
        domToReact(domNode.children, parserOptions),
      );
    },
  };

  return parserOptions;
};
